import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import { useAuth, routeAfterAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";

const LOGO = `${process.env.REACT_APP_BACKEND_URL}/api/brand/logo.png`;

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

// Keeps "?next=/path" from links like "Sign in to track your order"; only same-site paths.
function rememberNext() {
  const n = new URLSearchParams(window.location.search).get("next") || "";
  if (n.startsWith("/") && !n.startsWith("//")) sessionStorage.setItem("lif_after_login", n);
  else if (new URLSearchParams(window.location.search).has("next")) sessionStorage.removeItem("lif_after_login");
}

// Shoppers and gift givers without their own wedding plan land on their orders, not the plan picker.
async function customerHome(u) {
  if (u.plan_selected || u.role !== "couple") return null;
  if (u.is_customer) return "/account/orders";
  const r = await api.get("/invoices/mine").catch(() => null);
  return r?.data?.purchases?.length ? "/account/orders" : null;
}

export function Login() {
  const { login } = useAuth();
  React.useEffect(() => { rememberNext(); }, []);
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      rememberNext();
      const u = await login(email, password);
      toast.success("Welcome back");
      if (!sessionStorage.getItem("lif_after_login") && !u.plan_selected && u.role === "couple") {
        const sp = await api.get("/shared/plans").catch(() => ({ data: [] }));
        if (sp.data.length) { nav(`/shared/${sp.data[0].wedding_id}`); return; }
        const home = await customerHome(u);
        if (home) { nav(home); return; }
      }
      nav(sessionStorage.getItem("lif_after_login") ? (() => { const n = sessionStorage.getItem("lif_after_login"); sessionStorage.removeItem("lif_after_login"); return n; })() : routeAfterAuth(u));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Navbar />
      <div className="container-wide px-6 md:px-12 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="hidden lg:block luxe-image">
          <img src="https://images.pexels.com/photos/33403878/pexels-photo-33403878.jpeg?w=900" alt="" className="w-full h-[600px] object-cover" />
        </div>
        <div className="max-w-md mx-auto w-full">
          <img src={LOGO} alt="LockedInForever" className="h-16 mb-6" />
          <p className="font-script text-[var(--gold)] text-3xl mb-1">Welcome back</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl mb-8">Log in to your story</h1>

          <button onClick={googleLogin} data-testid="google-login-btn" className="w-full mb-4 flex items-center justify-center gap-3 border border-[var(--line)] rounded-full py-3 hover:bg-white transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.61z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.71A5.4 5.4 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35L15.02 2.34A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96L3.97 7.3C4.68 5.18 6.66 3.58 9 3.58z"/></svg>
            <span className="text-sm font-medium">Continue with Google</span>
          </button>

          <div className="relative my-6 text-center">
            <div className="h-px bg-[var(--line)]" />
            <span className="absolute inset-0 -top-2.5 mx-auto text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] bg-[var(--bg)] w-fit px-3">or with email</span>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Email</label>
              <input type="email" data-testid="login-email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-luxe" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Password</label>
              <input type="password" data-testid="login-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-luxe" />
            </div>
            <button data-testid="login-submit" disabled={busy} type="submit" className="btn-gold w-full">
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <p className="text-sm text-[var(--muted)] mt-6 text-center">
            New here? <Link to="/signup" className="text-[var(--gold)]">Create your wedding site</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("couple");
  const [refCode, setRefCode] = useState("");
  const [busy, setBusy] = useState(false);
  const seller = new URLSearchParams(window.location.search).get("seller") === "1";

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setRefCode(ref.toUpperCase());
    const r = params.get("role");
    if (["couple", "planner", "vendor", "guest"].includes(r)) setRole(r);
  }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      rememberNext();
      const u = await register({ name, email, password, role, referral_code: refCode || undefined });
      toast.success("Welcome to LockedInForever");
      const home = !sessionStorage.getItem("lif_after_login") && await customerHome(u);
      if (home) { nav(home); return; }
      nav(sessionStorage.getItem("lif_after_login") ? (() => { const n = sessionStorage.getItem("lif_after_login"); sessionStorage.removeItem("lif_after_login"); return n; })() : routeAfterAuth(u));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Navbar />
      <div className="container-wide px-6 md:px-12 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="max-w-md mx-auto w-full order-2 lg:order-1">
          <img src={LOGO} alt="LockedInForever" className="h-16 mb-6" />
          <p className="font-script text-[var(--gold)] text-3xl mb-1">Forever starts here</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl mb-8" data-testid="signup-title">{seller ? "Create your free seller account" : role === "guest" ? "Join the Social Center" : "Create your wedding site"}</h1>
          {seller && <p className="text-sm text-[var(--muted)] -mt-5 mb-6" data-testid="signup-seller-note">Posting on the Wedding Consignment marketplace is free. No plan or setup fee needed.</p>}

          <button onClick={googleLogin} data-testid="google-signup-btn" className="w-full mb-4 flex items-center justify-center gap-3 border border-[var(--line)] rounded-full py-3 hover:bg-white transition-colors">
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.61z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.71A5.4 5.4 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35L15.02 2.34A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96L3.97 7.3C4.68 5.18 6.66 3.58 9 3.58z"/></svg>
            <span className="text-sm font-medium">Continue with Google</span>
          </button>

          <div className="relative my-6 text-center">
            <div className="h-px bg-[var(--line)]" />
            <span className="absolute inset-0 -top-2.5 mx-auto text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] bg-[var(--bg)] w-fit px-3">or with email</span>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Your name</label>
              <input data-testid="signup-name" required value={name} onChange={(e) => setName(e.target.value)} className="input-luxe" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Email</label>
              <input type="email" data-testid="signup-email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-luxe" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Password</label>
              <input type="password" data-testid="signup-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="input-luxe" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] block mb-2">I am a…</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { v: "couple", l: "Couple" },
                  { v: "guest", l: "Guest" },
                  { v: "planner", l: "Planner" },
                  { v: "vendor", l: "Vendor" },
                ].map((r) => (
                  <button
                    key={r.v}
                    type="button"
                    data-testid={`role-${r.v}`}
                    onClick={() => setRole(r.v)}
                    className={`py-3 rounded-full text-xs uppercase tracking-wider transition-all ${
                      role === r.v ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] text-[var(--muted)] hover:border-[var(--gold)]"
                    }`}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
            {refCode && (
              <div className="bg-[var(--bg-warm)] rounded-xl px-4 py-3 text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--gold)]" />
                <span>Joining with referral code <span className="font-mono font-semibold">{refCode}</span></span>
              </div>
            )}
            <button disabled={busy} type="submit" data-testid="signup-submit" className="btn-gold w-full">
              {busy ? "Creating…" : "Create Account"}
            </button>
          </form>
          <p className="text-sm text-[var(--muted)] mt-6 text-center">
            Already have an account? <Link to="/login" className="text-[var(--gold)]">Log in</Link>
          </p>
        </div>
        <div className="hidden lg:block luxe-image order-1 lg:order-2">
          <img src="https://images.pexels.com/photos/35985203/pexels-photo-35985203.jpeg?w=900" alt="" className="w-full h-[600px] object-cover" />
        </div>
      </div>
    </div>
  );
}
